import { useId, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { Button } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { altDisplay } from '@/utilities/browser/specialKeys';

import { useButtonTabsShortcuts } from './useButtonTabsShortcuts';

export interface ButtonTabsItem {
    children: ReactNode;
    disabled?: boolean;
    icon?: ReactNode;
    key: string;
    label: ReactNode;
}

export interface ButtonTabsProps {
    activeKey?: string;
    ariaLabel?: string;
    className?: string;
    defaultActiveKey?: string;
    items: readonly ButtonTabsItem[];
    onChange?: (key: string) => void;
    style?: CSSProperties;
    tabBarStyle?: CSSProperties;
}

/** Compact button navigation with a bounded flex panel. Only the active item's content is mounted. */
const ButtonTabs = ({
    activeKey,
    ariaLabel = 'Sections',
    className,
    defaultActiveKey,
    items,
    onChange,
    style,
    tabBarStyle,
}: ButtonTabsProps): JSX.Element => {
    const { token } = useAntDesignTheme();
    const id = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const tabListRef = useRef<HTMLDivElement>(null);
    const [internalKey, setInternalKey] = useState(defaultActiveKey);
    const enabledItems = items.filter((item) => !item.disabled);
    const requestedKey = activeKey ?? internalKey;
    const selectedKey = enabledItems.find((item) => item.key === requestedKey)?.key ?? enabledItems[0]?.key;
    const tabId = (key: string) => `${id}-tab-${encodeURIComponent(key)}`;
    const panelId = (key: string) => `${id}-panel-${encodeURIComponent(key)}`;

    const select = (key: string) => {
        if (key === selectedKey || !enabledItems.some((item) => item.key === key)) {
            return;
        }
        if (activeKey === undefined) {
            setInternalKey(key);
        }
        onChange?.(key);
    };

    const navigate = (event: KeyboardEvent<HTMLElement>, key: string) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !enabledItems.length) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const index = enabledItems.findIndex((item) => item.key === key);
        const direction = window.getComputedStyle(event.currentTarget).direction;
        const forwardKey = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
        const nextIndex =
            event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? enabledItems.length - 1
                  : (index + (event.key === forwardKey ? 1 : -1) + enabledItems.length) % enabledItems.length;
        const next = enabledItems[nextIndex];
        const button =
            tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)')[nextIndex];
        button?.focus({ preventScroll: true });
        button?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        select(next.key);
    };

    useButtonTabsShortcuts(rootRef, items, select);

    return (
        <div
            ref={rootRef}
            className={className}
            data-button-tabs='true'
            style={{
                background: token.colorBgContainer,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                ...style,
            }}
        >
            <div
                ref={tabListRef}
                aria-label={ariaLabel}
                aria-orientation='horizontal'
                role='tablist'
                style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${token.colorBorder}`,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flex: 'none',
                    gap: '0.25rem',
                    height: '2.0625rem',
                    overflowX: 'auto',
                    paddingInline: '0.75rem',
                    ...tabBarStyle,
                }}
            >
                {items.map((item, index) => {
                    const selected = item.key === selectedKey;
                    const shortcut = !item.disabled && index < 9 ? `${altDisplay} + ${index + 1}` : undefined;
                    return (
                        <SmallTooltip key={item.key} title={item.label} placement='topLeft' shortcut={shortcut}>
                            <Button
                                aria-controls={panelId(item.key)}
                                aria-selected={selected}
                                aria-keyshortcuts={shortcut ? `Alt+${index + 1}` : undefined}
                                disabled={item.disabled}
                                id={tabId(item.key)}
                                onClick={() => select(item.key)}
                                onKeyDown={(event) => navigate(event, item.key)}
                                role='tab'
                                size='small'
                                style={{ background: selected ? token.colorFillSecondary : undefined, flexShrink: 0 }}
                                tabIndex={selected ? 0 : -1}
                                type='default'
                            >
                                <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                                    {item.icon}
                                    <small
                                        style={{
                                            alignItems: 'center',
                                            display: 'inline-flex',
                                            gap: '0.25rem',
                                            lineHeight: 1,
                                        }}
                                    >
                                        {item.label}
                                    </small>
                                </span>
                            </Button>
                        </SmallTooltip>
                    );
                })}
            </div>
            {items.map((item) => {
                const selected = item.key === selectedKey;
                return (
                    <div
                        aria-labelledby={tabId(item.key)}
                        hidden={!selected}
                        id={panelId(item.key)}
                        key={item.key}
                        role='tabpanel'
                        tabIndex={0}
                        style={{
                            display: selected ? 'flex' : 'none',
                            flex: '1 1 0%',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {selected ? item.children : null}
                    </div>
                );
            })}
        </div>
    );
};

export default ButtonTabs;
